import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Box,
  Button,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Typography,
  Chip,
  Container,
  useMediaQuery,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import { useAuth } from '../../../context/AuthContext';
import { useSalonAccess } from '../../../context/SalonAccessContext';
import { useMenuItems } from '../SideBar/MenuItems';
import { theme } from '../../../theme';
import NotificationsDropdown from '../../client/header/notifications';
import { useTranslation } from 'react-i18next';
import LanguageSelector from '../../LanguageSelector/LanguageSelector';

const NavBar = ({ children }) => {
  const muiTheme = useTheme();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { canSee } = useSalonAccess();
  const { menuItems, isActive } = useMenuItems();
  const isMdDown = useMediaQuery(muiTheme.breakpoints.down('md'));
  const isTabletWidth = useMediaQuery('(max-width:1366px)');
  const isCoarseTablet = useMediaQuery('(min-width:900px) and (max-width:1366px) and (pointer: coarse)');
  const isAppleTouchDevice = typeof navigator !== 'undefined'
    && navigator.maxTouchPoints > 1
    && /Mac|iPad|iPhone|iPod/.test(`${navigator.platform} ${navigator.userAgent}`);
  const isMobile = isMdDown || isCoarseTablet || (isTabletWidth && isAppleTouchDevice);
  const { t, i18n } = useTranslation();

  // Determine logo based on language
  const logoSrc = i18n.language === 'ar' ? '/assets/logo_ar.png' : '/assets/logo.png';

  const [anchorElUser, setAnchorElUser] = useState(null);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const canOpenManagerAccess = user?.role === 'professional_responsable'
    || canSee('managers.view');

  const supportMenuItem = menuItems.find((item) => item.path === '/pro/messaging');
  const primaryMenuItems = menuItems.filter((item) => item.path !== '/pro/messaging');

  const handleManagerAccessClick = () => {
    handleCloseUserMenu();
    navigate('/pro/gestionnaires');
  };

  const handleLogout = async () => {
    handleCloseUserMenu();
    try {
      await logout();
      // Navigation is handled by ProtectedRoute once user state becomes null
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleMenuItemClick = (path) => {
    navigate(path);
    if (isMobile) {
      setMobileDrawerOpen(false);
    }
  };

  const handleMobileDrawerToggle = () => {
    setMobileDrawerOpen(!mobileDrawerOpen);
  };

  // Desktop Navigation Items
  const DesktopNavItems = () => (
    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', justifyContent: 'center', flex: 1 }}>
      {primaryMenuItems.map((item) => {
        const active = isActive(item.path);
        return (
          <Button
            key={item.text}
            onClick={() => handleMenuItemClick(item.path)}
            startIcon={item.icon}
            sx={{
              color: active ? theme.colors.primary[700] : theme.colors.text.secondary,
              backgroundColor: active ? alpha(theme.colors.primary[500], 0.12) : 'transparent',
              borderRadius: 3,
              px: 2,
              py: 1.5,
              fontWeight: active ? 700 : 500,
              fontSize: '1rem',
              textTransform: 'none',
              position: 'relative',
              transition: 'all 0.3s ease',
              minWidth: 100,
              '&::before': {
                content: '""',
                position: 'absolute',
                bottom: 0,
                left: '50%',
                transform: 'translateX(-50%)',
                width: active ? '70%' : '0%',
                height: 3,
                backgroundColor: theme.colors.primary[500],
                borderRadius: '3px 3px 0 0',
                transition: 'width 0.3s ease',
              },
              '&:hover': {
                backgroundColor: alpha(theme.colors.primary[500], 0.08),
                color: theme.colors.primary[600],
                '&::before': {
                  width: '70%',
                },
              },
            }}
          >
            {item.text}
          </Button>
        );
      })}
    </Box>
  );

  // Mobile Drawer Content
  const MobileDrawer = () => (
    <Drawer
      anchor="left"
      open={mobileDrawerOpen}
      onClose={handleMobileDrawerToggle}
      sx={{
        '& .MuiDrawer-paper': {
          width: 280,
          backgroundColor: theme.colors.background.primary,
        },
      }}
    >
      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', borderBottom: `1px solid ${theme.colors.primary[200]}` }}>
        <img
          src={logoSrc}
          alt="Saloone Pro"
          style={{
            height: '40px',
            width: 'auto',
            marginBottom: '8px'
          }}
        />
        <Typography variant="caption" sx={{ color: theme.colors.text.secondary }}>
          {t('pro.space')}
        </Typography>
      </Box>
      
      <List sx={{ px: 2, py: 2 }}>
        {primaryMenuItems.map((item) => {
          const active = isActive(item.path);
          return (
            <ListItem key={item.text} disablePadding sx={{ mb: 1 }}>
              <ListItemButton
                onClick={() => handleMenuItemClick(item.path)}
                sx={{
                  borderRadius: 2,
                  backgroundColor: active ? alpha(theme.colors.primary[500], 0.12) : 'transparent',
                  color: active ? theme.colors.primary[700] : theme.colors.text.secondary,
                  fontWeight: active ? 600 : 400,
                  '&:hover': {
                    backgroundColor: alpha(theme.colors.primary[500], 0.08),
                  },
                }}
              >
                <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={item.text}
                  primaryTypographyProps={{
                    fontWeight: active ? 600 : 400,
                  }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
      
      <Divider sx={{ mt: 'auto' }} />
      
      <Box sx={{ p: 2 }}>
        <Button
          fullWidth
          variant="outlined"
          startIcon={<LogoutIcon />}
          onClick={handleLogout}
          sx={{
            borderColor: theme.colors.primary[300],
            color: theme.colors.primary[700],
            fontWeight: 600,
            '&:hover': {
              borderColor: theme.colors.primary[500],
              backgroundColor: alpha(theme.colors.primary[500], 0.05),
            },
          }}
        >
          {t('actions.logout')}
        </Button>
      </Box>
    </Drawer>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* AppBar */}
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          backgroundColor: 'white',
          borderBottom: `1px solid ${theme.colors.primary[200]}`,
          boxShadow: '0 2px 16px rgba(0,0,0,0.04)',
        }}
      >
        <Container maxWidth="xl">
          <Toolbar sx={{ justifyContent: 'space-between', py: 1.5, display: 'flex', gap: 2 }}>
            {/* Left: Logo */}
            <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 180 }}>
              {/* Mobile Menu Icon */}
              {isMobile && (
                <IconButton
                  onClick={handleMobileDrawerToggle}
                  sx={{ 
                    color: theme.colors.primary[700],
                    mr: 1,
                  }}
                >
                  <MenuIcon />
                </IconButton>
              )}
              
              {/* Logo */}
              <Box
                onClick={() => {
                  // Redirect to appropriate dashboard based on user role
                  if (user?.role === 'professional_employe') {
                    navigate('/pro/employe/dashboard');
                  } else {
                    navigate('/pro/dashboard');
                  }
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  cursor: 'pointer',
                  transition: 'transform 0.2s',
                  '&:hover': {
                    transform: 'scale(1.02)',
                  },
                }}
              >
               
                <Box>
                  <img
                    src={logoSrc}
                    alt="Saloone Pro"
                    style={{
                      height: '45px',
                      width: 'auto',
                      marginBottom: '4px'
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      color: theme.colors.text.secondary,
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      display: 'block',
                      lineHeight: 1,
                      mt: -0.5,
                    }}
                  >
                    PRO
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Center: Desktop Navigation */}
            {!isMobile && <DesktopNavItems />}

            {/* Right: User Menu */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0, minWidth: { xs: 'auto', sm: 180 }, justifyContent: 'flex-end' }}>
              <LanguageSelector sx={{ minWidth: 60 }} />
              <Box
                sx={{
                  color: theme.colors.text.secondary,
                  '&:hover': {
                    color: theme.colors.primary[600],
                  },
                }}
              >
                <NotificationsDropdown />
              </Box>
              {/* User Menu */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ display: { xs: 'none', sm: 'block' }, textAlign: 'right' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: theme.colors.text.primary }}>
                    {user?.first_name} {user?.last_name}
                  </Typography>
                  <Chip
                    label={user?.role === 'professional_employe' ? t('user.employee') : t('user.manager')}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      backgroundColor: alpha(theme.colors.secondary[500], 0.12),
                      color: theme.colors.secondary[700],
                    }}
                  />
                </Box>
                <IconButton onClick={handleOpenUserMenu} sx={{ p: 0.5 }}>
                  <Avatar
                    src={user?.profile_photo ? user.profile_photo : undefined}
                    sx={{
                      width: { xs: 36, sm: 40 },
                      height: { xs: 36, sm: 40 },
                      background: `linear-gradient(135deg, ${theme.colors.primary[500]} 0%, ${theme.colors.secondary[500]} 100%)`,
                      fontWeight: 700,
                    }}
                  >
                    {!user?.profile_photo && `${user?.first_name?.[0] || ''}${user?.last_name?.[0] || ''}`}
                  </Avatar>
                </IconButton>
              </Box>

              {/* User Dropdown Menu */}
              <Menu
                anchorEl={anchorElUser}
                open={Boolean(anchorElUser)}
                onClose={handleCloseUserMenu}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{
                  sx: {
                    mt: 1.5,
                    minWidth: 220,
                    borderRadius: 2,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                  },
                }}
              >
                <MenuItem onClick={() => { handleCloseUserMenu(); navigate('/pro/profile'); }}>
                  <ListItemIcon>
                    <PersonIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>{t('user.profile')}</ListItemText>
                </MenuItem>
                {supportMenuItem && (
                  <MenuItem onClick={() => {
                    handleCloseUserMenu();
                    navigate(supportMenuItem.path);
                  }}>
                    <ListItemIcon>{supportMenuItem.icon}</ListItemIcon>
                    <ListItemText>{supportMenuItem.text}</ListItemText>
                  </MenuItem>
                )}
                {canOpenManagerAccess && (
                  <MenuItem onClick={handleManagerAccessClick}>
                    <ListItemIcon>
                      <AdminPanelSettingsOutlinedIcon fontSize="small" color="primary" />
                    </ListItemIcon>
                    <ListItemText>{t('pro_menu.managers')}</ListItemText>
                  </MenuItem>
                )}
                <Divider sx={{ my: 1 }} />
                <MenuItem onClick={handleLogout}>
                  <ListItemIcon>
                    <LogoutIcon fontSize="small" color="error" />
                  </ListItemIcon>
                  <ListItemText sx={{ color: 'error.main' }}>{t('actions.logout')}</ListItemText>
                </MenuItem>
              </Menu>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Drawer */}
      <MobileDrawer />

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          backgroundColor: alpha(theme.colors.primary[50], 0.3),
          minHeight: 'calc(100vh - 80px)',
          py: 3,
        }}
      >
        <Container maxWidth="xl">
          {children}
        </Container>
      </Box>
    </Box>
  );
};

export default NavBar;
